import { GetPublicOverviewResponse } from '@orbit/api-types'
import { isValidCalendarDate } from '@lib/datetime'
import { isInlineImageMime, isPdfMime } from '@lib/mime'
import { formatMoney } from '@lib/money'
import {
	buildDateRange,
	buildPreviewDays,
	isOnTripDay,
	MAX_PREVIEW_FILES,
	MAX_PREVIEW_IDEAS,
	MAX_PREVIEW_STAYS,
	stayDateRange,
	sumInCurrency,
	toOverviewFlight,
	toTraveler,
} from './overviewShared'
import { OverviewFile, OverviewIdea, OverviewStay, TripOverviewData, TripOverviewSection } from './types'

/**
 * Normalize the aggregated Shared-trip endpoint into the same `TripOverviewData`
 * the member page renders from. Exposure is gated server-side; as
 * defense-in-depth this adapter re-checks the money flag before mapping named
 * balances/pay ups.
 */
export const buildPublicTripOverviewData = (response: GetPublicOverviewResponse): TripOverviewData => {
	const { trip, travelers, memberCount, sections } = response

	const startDate = trip.startDate
	const dayCount = trip.dayCount
	const hasDates = isValidCalendarDate(startDate) && dayCount > 0

	const summary = sections.expenses.summary
	const expensesShared = sections.expenses.shared

	return {
		hero: {
			name: trip.name,
			dayCount,
			hasDates,
			dateRange: buildDateRange(startDate, dayCount),
			travelers: travelers.map(toTraveler),
			memberCount,
		},
		itinerary: {
			hasDates,
			dayCount,
			// From the previewable slice, not the wire count: an out-of-range or
			// dateless payload must fall to `empty`, not render blank columns.
			eventCount: sections.itinerary.events.filter(event => isOnTripDay(event, dayCount)).length,
			previewDays: buildPreviewDays(
				startDate,
				dayCount,
				sections.itinerary.events,
				sections.itinerary.travelSegments,
			),
		},
		ideas: {
			count: sections.ideas.count,
			items: sections.ideas.ideas.slice(0, MAX_PREVIEW_IDEAS).map(
				(idea): OverviewIdea => ({
					id: String(idea.id),
					title: idea.name,
					thumbnailUrl: idea.thumbnailUrl ?? null,
				}),
			),
		},
		accommodations: {
			count: sections.accommodations.count,
			// The endpoint already returns these check-in ascending and capped.
			items: sections.accommodations.accommodations.slice(0, MAX_PREVIEW_STAYS).map(
				(stay): OverviewStay => ({
					id: String(stay.id),
					name: stay.name,
					city: stay.city ?? null,
					photoUrl: stay.photoUrl ?? null,
					dateRange: stayDateRange(stay.checkInDate, stay.checkOutDate),
				}),
			),
		},
		flights: {
			count: sections.flights.count,
			firstFlight: sections.flights.firstFlight
				? toOverviewFlight({
						airlineIata: sections.flights.firstFlight.airlineIata,
						flightNumber: sections.flights.firstFlight.flightNumber,
						originCode: sections.flights.firstFlight.departureAirportCode,
						destinationCode: sections.flights.firstFlight.arrivalAirportCode,
						// Public flights are exposed as wall-clock strings only.
						departureTime: null,
						departureTimeStr: sections.flights.firstFlight.departureTimeStr,
						durationMinutes: sections.flights.firstFlight.durationMinutes,
					})
				: null,
		},
		files: buildPublicFiles(
			sections.files.files,
			// Detail shows the returned slice, so count must equal it; a teaser
			// instead advertises the true total from the wire.
			sections.files.shared ? sections.files.files.length : sections.files.count,
		),
		money: {
			expenseCount: sections.expenses.count,
			hasExpenses: sections.expenses.count > 0,
			currencyCode: summary.currencyCode,
			expenseTotal: formatMoney(summary.groupTotal, summary.currencyCode),
			// Defense-in-depth: a malformed payload carrying pay ups alongside
			// `shared: false` must still produce an anonymous tile.
			payUpCount: expensesShared ? (sections.expenses.payUps?.length ?? 0) : 0,
			payUpTotal: formatMoney(
				expensesShared ? sumInCurrency(sections.expenses.payUps ?? [], summary.currencyCode) : 0,
				summary.currencyCode,
			),
		},
	}
}

const buildPublicFiles = (
	files: GetPublicOverviewResponse['sections']['files']['files'],
	count: number,
): TripOverviewData['files'] => ({
	count,
	items: files.slice(0, MAX_PREVIEW_FILES).map(
		(file): OverviewFile => ({
			id: file.id,
			fileName: file.fileName,
			url: file.url ?? null,
			// Same mime gate as the member page; non-images fall back to the icon chip.
			thumbnailUrl: isInlineImageMime(file.mimeType) ? (file.url ?? null) : null,
			isPdf: isPdfMime(file.mimeType),
		}),
	),
})

/** Per-section share flags for `resolveTileState`. Note the `expenses → money` remap. */
export const buildPublicShareFlags = (
	response: GetPublicOverviewResponse,
): Record<TripOverviewSection, boolean> => ({
	itinerary: response.sections.itinerary.shared,
	ideas: response.sections.ideas.shared,
	accommodations: response.sections.accommodations.shared,
	flights: response.sections.flights.shared,
	files: response.sections.files.shared,
	money: response.sections.expenses.shared,
})
